import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import figureFormatter from '@/services/utils/figureFormatter';
import pdfHeaderFooter from '@/services/utils/pdf/pdfHeaderFooter';
import { useStore } from 'vuex';
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const pdfPrinter = () => {
    const { getHeader, getFooter } = pdfHeaderFooter();
    const store = useStore();
    const { commaFormat } = figureFormatter();

    const userName = store.state.user.user.name;

    const exportToPDF = (company, data, startDate, endDate, qrcode, filterText = 'Filters: All') => {
        const doc = {
            pageSize: 'A4',
            pageOrientation: 'landscape',
            pageMargins: [12, 100, 12, 80],
            header: getHeader(company),
            footer: getFooter(userName, qrcode),
            content: getContent(data, startDate, endDate, filterText),
            defaultStyle: { color: 'black', fontSize: getFontSize(data.month_heads) },
            info: { title: 'Installment Due Report' }
        }

        pdfMake.createPdf(doc).open();
    }

    const getFontSize = (monthHeads) => {
        if (monthHeads.length > 10) return 5.5;
        if (monthHeads.length > 8) return 6;
        if (monthHeads.length > 4) return 7;
        return 8;
    }

    const getDateRangeText = (startDate, endDate) => {
        if (! startDate) return 'Date: All'
        if (! endDate) return 'Date: All'
        return `Date: ${startDate} - ${endDate}`
    }

    const getContent = (data, startDate, endDate, filterText) => {
        const { rows, totals, month_heads: monthHeads } = data;

        return [
            {
                text: 'Installment Due Report',
                fontSize: 14,
                bold: true,
                alignment: 'center',
                decoration: 'underline',
                margin: [0, 0, 0, 4]
            },
            { text: getDateRangeText(startDate, endDate), margin: [0, 0, 0, 2] },
            { text: filterText, margin: [0, 0, 0, 2] },
            {
                fontSize: getFontSize(monthHeads),
                table: {
                    headerRows: 1,
                    widths: getColumnWidths(monthHeads),
                    body: getTableBody(rows, totals, monthHeads)
                },
                layout: {
                    fillColor: (rowIndex) => {
                        if (rowIndex === 0) return '#f3f2f7'
                        return null
                    },
                    hLineWidth: () => 0.5,
                    vLineWidth: () => 0.5,
                    paddingLeft: () => 1.5,
                    paddingRight: () => 1.5,
                    paddingTop: () => 1,
                    paddingBottom: () => 1
                }
            }
        ]
    }

    const getColumnWidths = (monthHeads) => {
        const widths = [12, 22, 44, 40, 48, 'auto', 'auto', 'auto', 28, 'auto'];
        return widths.concat(monthHeads.map(() => '*'));
    }

    const cleanText = (value) => {
        if (! value) return ''
        return value
    }

    const getEmployeeLabel = (row) => {
        if (! row.employee_name) return ''
        return `${row.employee_name} (${row.employee_id})`
    }

    const getCell = (row, key) => {
        if (! row.months) return 0
        if (! row.months[key]) return 0
        return row.months[key]
    }

    const getTotalCell = (totals, key) => {
        if (! totals.months) return 0
        if (! totals.months[key]) return 0
        return totals.months[key]
    }

    const monthAmountText = (value) => {
        if (! value) return '-'
        return commaFormat(value, 0)
    }

    const getTableBody = (rows, totals, monthHeads) => {
        const header = [
            { text: 'SL', bold: true },
            { text: 'ID', bold: true },
            { text: 'Shareholder Name', bold: true },
            { text: 'Mobile No', bold: true },
            { text: 'Marketing Team (Ref Name & ID)', bold: true },
            { text: 'Total Payable', bold: true, alignment: 'right' },
            { text: 'Booking / Down Payment', bold: true, alignment: 'right' },
            { text: 'Remaining', bold: true, alignment: 'right' },
            { text: 'No Of Inst.', bold: true, alignment: 'right' },
            { text: 'Monthly Installment', bold: true, alignment: 'right' },
            ...monthHeads.map((head) => ({ text: head.label, bold: true, alignment: 'right' }))
        ];

        const dataRows = rows.map((row, index) => [
            { text: index + 1 },
            { text: cleanText(row.serial_no) },
            { text: cleanText(row.shareholder_name) },
            { text: cleanText(row.mobile_no) },
            { text: getEmployeeLabel(row) },
            { text: commaFormat(row.total_payable), alignment: 'right' },
            { text: commaFormat(row.booking_amount), alignment: 'right' },
            { text: commaFormat(row.remaining), alignment: 'right' },
            { text: row.number_of_installment, alignment: 'right' },
            { text: commaFormat(row.monthly_installment), alignment: 'right' },
            ...monthHeads.map((head) => ({
                text: monthAmountText(getCell(row, head.key)),
                alignment: 'right'
            }))
        ]);

        const totalRow = [
            { text: 'Total', bold: true, colSpan: 5 }, {}, {}, {}, {},
            { text: commaFormat(totals.total_payable), bold: true, alignment: 'right' },
            { text: commaFormat(totals.booking_amount), bold: true, alignment: 'right' },
            { text: commaFormat(totals.remaining), bold: true, alignment: 'right' },
            { text: totals.number_of_installment, bold: true, alignment: 'right' },
            { text: commaFormat(totals.monthly_installment), bold: true, alignment: 'right' },
            ...monthHeads.map((head) => ({
                text: monthAmountText(getTotalCell(totals, head.key)),
                bold: true,
                alignment: 'right'
            }))
        ];

        return [header, ...dataRows, totalRow];
    }

    return { exportToPDF }
}

export default pdfPrinter;
